import { matchers, validate } from "../validate-props";
/**
 * Returns if the given data is an array with arrays.
 * Ex: [[],[],[]]
 * @param {Array.<Array>} data - must have only array children
 * @returns {Boolean}
 */
export const validateDataGrid = (data) => {
    if (!matchers.array(data)) {
        return false;
    }
    if (!data.length) {
        return false;
    }
    // Data has only arrays in it?
    return data.reduce((onlyArray, item) => {
        return (onlyArray = onlyArray && matchers.array(item));
    }, true);
};

export const defaultProps = Object.freeze({
    data: undefined,
    rowsPerPage: undefined,
    startPage: 0,
    totalPages: undefined,
});

export const schema = Object.freeze({
    data: matchers.array,
    rowsPerPage: matchers.or(matchers.undefined, matchers.number),
    startPage: matchers.number,
    totalPages: matchers.or(matchers.undefined, matchers.number),
});

/**
 * Create a datagrid object to handle 2 dimension lists.
 * @param {defaultProps} defaultProps
 * @returns {Object} public api
 */
const DataGrid = (props) => {
    const mergedProps = Object.assign({}, defaultProps, props);
    const [pass, report] = validate(mergedProps, schema);
    if (!pass) {
        console.warn(`DataGrid got invalid props. ${JSON.stringify(report)}`);
        return;
    }

    /**
     * Current datagrid
     * @returns {Array}
     */
    const getDataGrid = () => {
        return _dataGrid;
    };

    /**
     *
     * @param {Array} newData Array with only Array as content.
     * Ex: [[],[]]
     * @returns {Boolean}
     */
    const setDataGrid = (newData) => {
        if (!validateDataGrid(newData)) {
            return false;
        }
        _dataGrid = newData.reduce((grid, row) => {
            grid.push(row.concat([]));
            return grid;
        }, []);
        return true;
    };

    /**
     * Append rows to the current grid;
     * @param {Array} newData
     * @returns {Boolean}
     */
    const appendDataGrid = (newData) => {
        if (!validateDataGrid(newData)) return false;
        newData.forEach(appendRow);
        return true;
    };

    /**
     * Prepend new datagrid;
     * @param {Array} newData
     * @returns {Boolean}
     */
    const prependDataGrid = (newData) => {
        if (!validateDataGrid(newData)) return false;
        _dataGrid = newData.concat(_dataGrid);
        return true;
    };

    //
    // ROW
    //

    /**
     * Append rows to the current grid;
     * @param {Array} newData
     * @returns {Boolean}
     */
    const appendRow = (newRow) => {
        if (!matchers.array(newRow)) return false;
        _dataGrid.push(newRow.concat([]));
        return true;
    };

    /**
     * Deletes the **row** from data grid
     * @param {Number} row
     * @returns {Array}
     */
    const deleteRow = (row) => {
        if (!hasRow(row)) {
            return false;
        }
        _dataGrid.splice(row, 1);
        return true;
    };

    /**
     * Returns all cols for given **row**
     * @param {Number} row
     * @returns {Array}
     */
    const getRow = (row = 0) => {
        if (!matchers.number(row) || row < 0 || row > getTotalRows()) {
            return null;
        }
        return row < getTotalRows() ? getDataGrid()[row] : null;
    };

    /**
     * Total data grid rows
     * @returns {Number}
     */
    const getTotalRows = () => {
        if (matchers.number(_totalPages)) {
            return _totalPages * rowsPerPage;
        }
        return getDataGrid().length;
    };

    /**
     * Return if **row** exists
     * @param {Number} row
     * @returns {Boolean}
     */
    const hasRow = (row) => {
        return matchers.number(row) && row >= 0 && row < getTotalRows();
    };

    /**
     * Adds newRow in the beginning of the grid
     * @param {Array} newData
     * @returns {Boolean}
     */
    const prependRow = (newRow) => {
        if (!Array.isArray(newRow)) return false;
        _dataGrid.unshift(newRow);
        return true;
    };

    //
    // COL
    //
    /**
     * Append new column with **data**
     * @param {*} data
     * @return {Boolean}
     */
    const appendCol = (data) => {
        _dataGrid = _dataGrid.map((row) => {
            row.push(data);
            return row;
        });
        return true;
    };

    /**
     * Delete the **col** on every row
     * @param {Number} col
     * @returns {Boolean}
     */
    const deleteCol = (col) => {
        if (!hasCol(col)) {
            return false;
        }
        _dataGrid = _dataGrid.map((row) => {
            row.splice(col, 1);
            return row;
        });
        return true;
    };

    /**
     * Returns all rows for given **col**
     * @param {Number} col
     * @returns {Array}
     */
    const getCol = (col = 0) => {
        if (!matchers.number(col) || col < 0 || col >= getTotalCols()) {
            return null;
        }
        return getDataGrid().reduce((cols, row) => {
            cols.push(row[col]);
            return cols;
        }, []);
    };

    /**
     * Return if **col** exists
     * @param {Number} row
     * @returns {Boolean}
     */
    const hasCol = (col) => {
        return matchers.number(col) && col >= 0 && col < getTotalCols();
    };

    /**
     * Returns the size of the row with more columns
     * @returns {Number}
     */
    const getTotalCols = () => {
        return getDataGrid().reduce((maxCol, row) => {
            return Math.max(maxCol, row.length);
        }, 0);
    };

    /**
     * Add **data** at the beginning of every row
     * @param {*} data
     * @returns {Boolean}
     */
    const prependCol = (data) => {
        _dataGrid = _dataGrid.map((row) => {
            row.unshift(data);
            return row;
        });
        return true;
    };

    /**
     * Returns the rows per page
     * @returns {Number}
     */
    const getRowsPerPage = () => {
        return _rowsPerPage || _dataGrid.length;
    };

    /**
     * Set the number of items each page has
     * @param {Number} value
     * @returns {Boolean}
     */
    const setRowsPerPage = (value) => {
        if (!matchers.number(value) || value < 0) return false;
        _rowsPerPage = value;
        return true;
    };

    //
    // PAGE
    //
    /**
     * Returns the current index of the page.
     * @returns {Number}
     */
    const getCurrentPage = () => {
        return _currentPageIndex;
    };

    /**
     * Set current page
     * @param {Number} page
     * @returns
     */
    const setCurrentPage = (page) => {
        if (!hasPage(page)) return false;
        _currentPageIndex = page;
        return true;
    };

    /**
     * Checks if page exists according to the rowsPerPage
     * @param {Number} page
     * @returns {Boolean}
     */
    const hasPage = (page) => {
        return matchers.number(page) && matchers.inRange(1, getTotalPages())(page);
    };

    /**
     * Returns the total of pages according to:
     * - rowsPerPage and data size
     * - totalPages defined on initialization or setTotalPages
     * @returns {Number}
     */
    const getTotalPages = () => {
        return matchers.number(_totalPages)
            ? _totalPages
            : Math.ceil(Math.max(1, getTotalRows() / getRowsPerPage()));
    };
    /**
     * Update totalPages.
     * This overrides the dynamic getTotalPages based on data size
     * @param {Number} total
     * @returns {Boolean}
     */
    const setTotalPages = (total) => {
        if (matchers.or(matchers.undefined, matchers.number)(total)) {
            _totalPages = total;
            return true;
        }
        return false;
    };
    /**
     * Returns true when currentPage < totalPages
     * @returns {Boolean}
     */
    const hasNextPage = () => {
        return hasPage(getCurrentPage() + 1);
    };

    /**
     * Returns false when currentPage = 0
     * @returns {Boolean}
     */
    const hasPrevPage = () => {
        return hasPage(getCurrentPage() - 1);
    };

    /**
     * Bump the current page and return the index
     * @returns {Number}
     */
    const nextPage = () => {
        if (!hasNextPage()) return false;
        _currentPageIndex++;
        return true;
    };

    /**
     * Decrease the current page and return the index
     * @returns {Number}
     */
    const prevPage = () => {
        if (!hasPrevPage()) return false;
        _currentPageIndex--;
        return true;
    };

    /**
     * Returns a data grid for **page**
     * @param {Number} page
     * @returns {Array}
     */
    const getPageData = (page) => {
        page = matchers.number(page) ? page : matchers.undefined(page) ? getCurrentPage() : null;
        if (!hasPage(page)) return null;
        const result = getDataGrid();
        const startSlice = _getIndexFromPage(page - 1);
        const endSlice = Math.min(startSlice + getRowsPerPage(), getDataGrid().length);
        if (startSlice > result.length) return null;
        return result.slice(startSlice, endSlice);
    };

    const getSummary = () => {
        const pageData = getPageData();
        const pageLength = Math.max(0, (pageData || []).length);
        const summary = {};
        summary.hasNextPage = hasNextPage();
        summary.hasPrevPage = hasPrevPage();
        summary.currentPage = getCurrentPage();
        summary.totalPages = getTotalPages();
        summary.totalRows = getTotalRows();
        summary.rowsPerPage = getRowsPerPage();
        summary.firstRowIndexOnPage = (summary.currentPage - 1) * summary.rowsPerPage;
        summary.lastRowIndexOnPage = summary.firstRowIndexOnPage + pageLength - 1;
        return summary;
    };

    const api = {
        //Datagrid
        appendDataGrid,
        getDataGrid,
        prependDataGrid,
        setDataGrid,

        //Row
        appendRow,
        deleteRow,
        getRow,
        getTotalRows,
        hasRow,
        prependRow,

        //Col
        appendCol,
        deleteCol,
        getCol,
        hasCol,
        getTotalCols,
        prependCol,

        //Page
        getCurrentPage,
        getPageData,
        getRowsPerPage,
        getTotalPages,
        hasNextPage,
        hasPage,
        hasPrevPage,
        nextPage,
        prevPage,
        setCurrentPage,
        setRowsPerPage,
        setTotalPages,

        //
        getSummary,
    };

    const _getIndexFromPage = (page) => {
        return Math.max(0, page * getRowsPerPage());
    };

    const { data, rowsPerPage, currentPage, totalPages } = mergedProps;

    // Private
    let _dataGrid;
    let _rowsPerPage = 0;
    let _currentPageIndex = 1;
    let _totalPages = null;

    // Init instance with args
    setDataGrid(data);
    setTotalPages(totalPages);
    setRowsPerPage(rowsPerPage);
    setCurrentPage(currentPage);

    return api;
};

export default DataGrid;
